import React, { FC, useEffect, useState } from 'react';
import { Box, Collapse, IconButton, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import useService from '../../../hooks/service.hook';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { useNavigate, useLocation } from 'react-router-dom';
import TeamsService from '../../../services/teams.service';
import { Team } from '../../../models/training.models';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { palette } from '../../../theme';
import {
  TEAM_DETAILS_STORAGE_KEYS,
  clearStoredValue,
  getStoredValue,
  setStoredValue,
} from '../../../helpers/cache.helpers';

interface TeamsLinkProps {
  isActive: boolean;
  sidebarOpen: boolean;
}

const TeamsLink: FC<TeamsLinkProps> = (props) => {
  // hooks
  const teamsService = useService(TeamsService);
  const navigate = useNavigate();
  const location = useLocation();

  // states
  const [teams, setTeams] = useState<Team[]>([]);
  const [open, setOpen] = useState<boolean>(false);

  // Refresh when route changes so cancel/delete don't leave a stale team list.
  useEffect(() => {
    getTeams();
  }, [location.pathname]);

  const getTeams = () => {
    teamsService.getTeams().then((response) => {
      const nextTeams = (response.data.data || []) as Team[];
      setTeams(nextTeams);
      const storedTeamId = getStoredValue(TEAM_DETAILS_STORAGE_KEYS.TEAM, '');
      if (!storedTeamId) return;
      const stillActive = nextTeams.some((team) => String(team.id) === storedTeamId && team.status === 'active');
      if (!stillActive) {
        clearStoredValue(TEAM_DETAILS_STORAGE_KEYS.TEAM);
      }
    });
  };

  const handleTeamSelected = (id: any) => {
    setStoredValue(TEAM_DETAILS_STORAGE_KEYS.TEAM, String(id));
    navigate(`/teams/${id}/dashboard`);
  };

  const handleMyTeamsClick = () => {
    const storedTeamId = getStoredValue(TEAM_DETAILS_STORAGE_KEYS.TEAM, '');
    teamsService
      .getTeams()
      .then((response) => {
        const nextTeams = (response.data.data || []) as Team[];
        setTeams(nextTeams);

        if (!storedTeamId) {
          navigate('/teams');
          return;
        }

        const matchingTeam = nextTeams.find((team) => String(team.id) === storedTeamId);
        if (matchingTeam && matchingTeam.status === 'active') {
          navigate(`/teams/${storedTeamId}/dashboard`);
          return;
        }

        clearStoredValue(TEAM_DETAILS_STORAGE_KEYS.TEAM);
        navigate('/teams');
      })
      .catch(() => {
        clearStoredValue(TEAM_DETAILS_STORAGE_KEYS.TEAM);
        navigate('/teams');
      });
  };

  const handleDropdownSelected = (evt: any) => {
    evt.stopPropagation();
    setOpen(!open);
  };

  return (
    <>
      <ListItemButton
        sx={{
          height: 40,
          overflow: 'hidden',
          mx: 1,
          borderRadius: '8px',
          backgroundColor: props.isActive ? palette.primary.light : 'transparent',
          '&:hover': { backgroundColor: 'rgba(209,225,255,0.4)' },
          ...(!props.sidebarOpen && {
            justifyContent: 'center',
            px: 0,
            '& .MuiListItemText-root': { display: 'none' },
            '& .MuiListItemIcon-root': { minWidth: 'unset', mr: 0, justifyContent: 'center' },
          }),
        }}
        onClick={handleMyTeamsClick}
      >
        <ListItemIcon sx={{ minWidth: 32, mr: 0.5 }}>
          <GroupsOutlinedIcon
            sx={{
              fontSize: 20,
              color: props.isActive ? palette.primary.main : palette.text.primary,
            }}
          />
        </ListItemIcon>
        <ListItemText
          primary={'My Teams'}
          primaryTypographyProps={{
            sx: {
              color: props.isActive ? palette.primary.main : palette.text.primary,
              fontSize: 12,
              fontWeight: props.isActive ? 600 : 400,
            },
          }}
        />
        {props.sidebarOpen && teams.length > 0 && (
          <IconButton
            onClick={handleDropdownSelected}
            style={{ color: props.isActive ? palette.primary.main : palette.text.primary, height: 24, width: 24 }}
          >
            {open ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        )}
      </ListItemButton>
      <Collapse in={props.sidebarOpen && open} timeout="auto" unmountOnExit>
        <Box sx={{ maxHeight: '250px', overflowY: 'auto' }}>
          <List component="div" disablePadding style={{ display: 'flex', flexDirection: 'column' }}>
            {teams
              .filter((team) => team.status === 'active')
              .map((team: Team) => {
                const isTeamActive = location.pathname.includes(`/teams/${team.id}/`);
                return (
                  <ListItemButton
                    key={team.id}
                    sx={{
                      height: 35,
                      mx: 1,
                      pl: 3,
                      borderRadius: '8px',
                      backgroundColor: isTeamActive ? palette.primary.light : 'transparent',
                      '&:hover': { backgroundColor: 'rgba(209,225,255,0.4)' },
                    }}
                    onClick={() => handleTeamSelected(team.id)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%' }}>
                      <Box
                        component="span"
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: team.color || palette.text.disabled,
                          flexShrink: 0,
                        }}
                      />
                      <ListItemText
                        primary={team.name}
                        primaryTypographyProps={{
                          sx: {
                            fontSize: '11px',
                            fontWeight: 400,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            color: isTeamActive ? palette.primary.main : palette.text.primary,
                          },
                        }}
                      />
                    </Box>
                  </ListItemButton>
                );
              })}
          </List>
        </Box>
      </Collapse>
    </>
  );
};

export default TeamsLink;
